import type { CSSProperties } from 'react'

import type { FlowGraph, FlowNode } from '../lib/getNodesAndEdges'

type LogicMapCanvasProps = {
  graph: FlowGraph
  selectedStepId?: string
  onSelectStep?: (stepId?: string) => void
}

const CANVAS_WIDTH = 980
const CANVAS_HEIGHT = 320
const NODE_WIDTH = 188
const NODE_HEIGHT = 92

export function LogicMapCanvas({ graph, selectedStepId, onSelectStep }: LogicMapCanvasProps) {
  return (
    <section
      style={{
        background: 'linear-gradient(180deg, rgba(10,16,24,0.96) 0%, rgba(6,10,16,0.98) 100%)',
        border: '1px solid rgba(148, 163, 184, 0.14)',
        borderRadius: 16,
        padding: 20,
        color: '#edf2f7',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#93a4b8',
              marginBottom: 6,
            }}
          >
            Live Logic Map
          </div>
          <div style={{ fontSize: 14, color: '#d7e2f0' }}>Directed execution view of the active run</div>
        </div>
        <div
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: 12,
            color: '#9fb0c6',
          }}
        >
          {graph.nodes.length} nodes · {graph.edges.length} edges
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          borderRadius: 14,
          border: '1px solid rgba(148, 163, 184, 0.1)',
          background:
            'radial-gradient(circle at top left, rgba(91, 192, 255, 0.06), transparent 32%), linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))',
          overflowX: 'auto',
          overflowY: 'hidden',
          paddingBottom: 4,
        }}
      >
        <svg width={Math.max(CANVAS_WIDTH, graph.nodes.length * 220)} height={CANVAS_HEIGHT} role="img" aria-label="Run logic map">
          <defs>
            <pattern id="logic-grid" width="28" height="28" patternUnits="userSpaceOnUse">
              <path d="M 28 0 L 0 0 0 28" fill="none" stroke="rgba(148,163,184,0.06)" strokeWidth="1" />
            </pattern>
            <filter id="logic-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="6" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#logic-grid)" />

          {graph.edges.map((edge) => {
            const source = graph.nodes.find((node) => node.id === edge.source)
            const target = graph.nodes.find((node) => node.id === edge.target)
            if (!source || !target) {
              return null
            }
            const x1 = source.position.x + NODE_WIDTH / 2
            const y1 = source.position.y + NODE_HEIGHT
            const x2 = target.position.x + NODE_WIDTH / 2
            const y2 = target.position.y
            const cx1 = x1
            const cy1 = y1 + 48
            const cx2 = x2
            const cy2 = y2 - 48
            return (
              <path
                key={edge.id}
                d={`M ${x1} ${y1} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${x2} ${y2}`}
                fill="none"
                stroke={edge.animated ? '#7ed0ff' : 'rgba(148, 163, 184, 0.44)'}
                strokeWidth="2"
                strokeDasharray={edge.animated ? '8 6' : undefined}
                filter={edge.animated ? 'url(#logic-glow)' : undefined}
              />
            )
          })}

          {graph.nodes.map((node) => {
            const isSelected = selectedStepId ? node.id === `step:${selectedStepId}` : false
            return (
              <foreignObject
                key={node.id}
                x={node.position.x}
                y={node.position.y}
                width={NODE_WIDTH}
                height={NODE_HEIGHT}
              >
                <button
                  type="button"
                  onClick={() => onSelectStep?.(node.data.kind === 'step' ? String(node.data.id) : undefined)}
                  style={nodeButtonStyle(node, isSelected)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#9db0c8', fontWeight: 800 }}>
                      {String(node.data.kind)}
                    </span>
                    <StatusPill color={String(node.data.color)} label={String(node.data.status)} />
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#f5f9ff', marginBottom: 8 }}>
                    {String(node.data.label)}
                  </div>
                  <div
                    style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: 11,
                      color: '#9fb0c6',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {String(node.data.id)}
                  </div>
                </button>
              </foreignObject>
            )
          })}
        </svg>
      </div>
    </section>
  )
}

function nodeButtonStyle(node: FlowNode, isSelected: boolean): CSSProperties {
  const color = String(node.data.color || '#6b7280')
  const animated = Boolean(node.data.animated)
  return {
    width: '100%',
    height: '100%',
    borderRadius: 14,
    border: `1px solid ${isSelected ? color : 'rgba(148, 163, 184, 0.16)'}`,
    background: isSelected
      ? 'linear-gradient(180deg, rgba(19,28,40,0.96), rgba(10,15,23,0.98))'
      : 'linear-gradient(180deg, rgba(17,25,36,0.94), rgba(9,14,21,0.98))',
    boxShadow: animated
      ? `0 0 0 1px ${color}44, 0 0 26px ${color}30`
      : isSelected
        ? `0 0 0 1px ${color}22, 0 14px 32px rgba(0,0,0,0.22)`
        : '0 12px 28px rgba(0,0,0,0.2)',
    padding: 14,
    textAlign: 'left',
    cursor: 'pointer',
    color: '#edf2f7',
  }
}

function StatusPill({ color, label }: { color: string; label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        background: `${color}18`,
        color,
        border: `1px solid ${color}33`,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: color,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  )
}
